import { formatDuration } from '../lib/format.js';

const PILLS = [
  { value: 'On Hold', label: 'On Hold', cls: 'border-warn/40 text-warn hover:bg-warn/10' },
  { value: 'Complete', label: 'Complete', cls: 'border-running/40 text-running hover:bg-running/10' },
  { value: 'Blocked', label: 'Blocked', cls: 'border-danger/40 text-danger hover:bg-danger/10' }
];

// `now` is threaded down from TimeTracker, which owns the single 1Hz
// tick driving every visible timer in the app. Each QueueCard used to
// own its own setInterval — the resulting drift between cards / PiP /
// the main chip was the user-visible "irregular updates" complaint.
function QueueCard({ item, busyAction, now, canMoveUp, canMoveDown, onResume, onPause, onComplete, onSetStatus, onOpen, onNext, onMoveUp, onMoveDown }) {
  let elapsed = item.elapsedSeconds;
  if (item.state === 'running' && item.startTime) {
    const tickNow = typeof now === 'number' ? now : Date.now();
    elapsed = Math.max(0, Math.floor((tickNow - new Date(item.startTime).getTime()) / 1000));
  }

  const isRunning = item.state === 'running';
  const longRunning = isRunning && elapsed > 7200;

  return (
    <div
      className={`rounded-lg border overflow-hidden transition-colors ${
        isRunning
          ? 'border-running/50 bg-running/15 shadow-[0_0_12px_rgba(34,197,94,0.15)]'
          : 'border-warn/50 bg-warn/15 shadow-[0_0_12px_rgba(245,158,11,0.12)]'
      }`}
    >
      <div className="px-3 py-2.5">
        {!isRunning && (canMoveUp || canMoveDown) && (
          <div className="flex items-center gap-1 -mt-0.5 mb-1 -mr-1 justify-end">
            {canMoveUp && (
              <button
                onClick={() => onMoveUp(item)}
                title="Move up"
                className="p-1 rounded hover:bg-panel2 text-muted hover:text-text"
              >
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M18 15l-6-6-6 6" />
                </svg>
              </button>
            )}
            {canMoveDown && (
              <button
                onClick={() => onMoveDown(item)}
                title="Move down"
                className="p-1 rounded hover:bg-panel2 text-muted hover:text-text"
              >
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </button>
            )}
          </div>
        )}
        <div className="flex items-start gap-2 mb-1.5">
          {isRunning ? (
            <div className="mt-1 w-2 h-2 rounded-full bg-running pulse-dot flex-shrink-0" />
          ) : (
            <div className="mt-1 w-2 h-2 rounded-full bg-warn flex-shrink-0" />
          )}
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-muted">
              {isRunning ? 'Active task' : 'On hold'}
            </div>
            <button
              onClick={() => onOpen(item)}
              className="block w-full text-left text-sm font-medium truncate hover:text-accent transition-colors"
              title={item.subTaskName}
            >
              {item.subTaskName || '(untitled)'}
            </button>
            {item.taskName && item.taskName !== item.subTaskName && (
              <div className="text-[11px] text-muted truncate mt-0.5">in {item.taskName}</div>
            )}
          </div>
        </div>

        <div className={`font-mono text-lg font-semibold ${longRunning ? 'text-warn' : isRunning ? 'text-running' : 'text-text/70'}`}>
          {formatDuration(elapsed)}
          {longRunning && <span className="ml-1 text-xs">⚠</span>}
        </div>

        {item.atMaxDuration && (
          <div className="mt-2 px-2.5 py-1.5 rounded border border-warn/40 bg-warn/10 text-[11px] text-warn">
            ⚠ Hit the 4h cap{item.autoClosed ? ' (auto-stopped by system)' : ''}. Start a new task to continue tracking.
          </div>
        )}

        <div className="mt-2.5 flex flex-wrap gap-1.5">
          {!isRunning && !item.atMaxDuration && (
            <button
              disabled={Boolean(busyAction)}
              onClick={() => onResume(item)}
              className="px-2.5 py-1 rounded text-[11px] font-medium border border-cyan-700/50 text-cyan-700 hover:bg-cyan-100 dark:border-cyan-500/40 dark:text-cyan-300 dark:hover:bg-cyan-500/10 transition-colors"
            >
              {busyAction === 'resume' ? '...' : '▶ Resume'}
            </button>
          )}
          {isRunning && (
            <button
              disabled={Boolean(busyAction)}
              onClick={() => onPause(item)}
              className="px-2.5 py-1 rounded text-[11px] font-medium border border-warn/40 text-warn hover:bg-warn/10 transition-colors"
            >
              {busyAction === 'pause' ? '...' : '⏸ On Hold'}
            </button>
          )}
          <button
            disabled={Boolean(busyAction)}
            onClick={() => onComplete(item)}
            className="px-2.5 py-1 rounded text-[11px] font-medium border border-running/40 text-running hover:bg-running/10 transition-colors"
          >
            {busyAction === 'complete' ? '...' : '✓ Complete'}
          </button>
          {!isRunning && (
            <button
              disabled={Boolean(busyAction)}
              onClick={() => onSetStatus(item, 'Blocked')}
              className="px-2.5 py-1 rounded text-[11px] font-medium border border-danger/40 text-danger hover:bg-danger/10 transition-colors"
            >
              ✕ Blocked
            </button>
          )}
        </div>

        {isRunning && item.nextSubTask && (
          <button
            disabled={Boolean(busyAction)}
            onClick={() => onNext(item)}
            className="mt-2 w-full flex items-center justify-between gap-2 px-3 py-2 rounded-md border border-accent/40 bg-accent/5 hover:bg-accent/10 transition-colors text-left"
          >
            <div className="min-w-0 flex-1">
              <div className="text-[9px] uppercase tracking-wider text-accent/80 font-semibold">
                Next sub-task
              </div>
              <div className="text-xs font-medium truncate text-text/90">{item.nextSubTask.name}</div>
            </div>
            <div className="flex items-center gap-1 text-accent text-xs font-semibold flex-shrink-0">
              {busyAction === 'next' ? '...' : (
                <>
                  Next
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M9 18l6-6-6-6" />
                  </svg>
                </>
              )}
            </div>
          </button>
        )}
      </div>
    </div>
  );
}

export default function TaskQueuePanel({ items, busyMap, now, onResume, onPause, onComplete, onSetStatus, onOpen, onNext, onMoveUp, onMoveDown, error }) {
  const safeItems = items || [];
  const sorted = [...safeItems].sort((a, b) => {
    if (a.state === 'running' && b.state !== 'running') return -1;
    if (b.state === 'running' && a.state !== 'running') return 1;
    return 0;
  });
  const pausedSorted = sorted.filter(i => i.state !== 'running');

  return (
    <div>
      <div className="flex items-center justify-between mb-2 px-1">
        <div className="text-[10px] uppercase tracking-wider text-muted font-semibold">
          Task Queue {sorted.length > 0 && `· ${sorted.length}`}
        </div>
      </div>

      {error && (
        <div className="mb-2 p-2 rounded border border-danger/30 bg-danger/10 text-xs text-danger">
          {error}
        </div>
      )}

      {sorted.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border bg-panel/40 p-6 text-center">
          <div className="text-xs text-muted leading-relaxed">
            Nothing in your queue yet.<br />
            Start a task or pick from the search list.
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          {sorted.map(item => {
            const pausedIdx = pausedSorted.findIndex(p => p.subTaskId === item.subTaskId);
            const canMoveUp = item.state !== 'running' && pausedIdx > 0;
            const canMoveDown = item.state !== 'running' && pausedIdx >= 0 && pausedIdx < pausedSorted.length - 1;
            return (
              <QueueCard
                key={item.subTaskId}
                item={item}
                busyAction={busyMap?.[item.subTaskId]}
                now={now}
                canMoveUp={canMoveUp}
                canMoveDown={canMoveDown}
                onResume={onResume}
                onPause={onPause}
                onComplete={onComplete}
                onSetStatus={onSetStatus}
                onOpen={onOpen}
                onNext={onNext}
                onMoveUp={onMoveUp}
                onMoveDown={onMoveDown}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
